import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { css } from "@emotion/css";
import { usePublishedQuestionnaireHistory, usePublishToGroup } from "../queries";
import { RevisionRollout } from "../api";
import RolloutStatusChip from "./RolloutStatusChip";
import { deployAttribution, groupLabel, isDeployTarget, timeAgo } from "../rolloutText";

const groupEnvsLabel = (group: string) => {
  if (group === "prod") return "EU Prod and US Prod";
  if (group === "stage") return "EU Stage and US Stage";
  return groupLabel(group);
};

const chipTooltip = (entry: RevisionRollout) => {
  const envs = groupEnvsLabel(entry.group);
  const attribution = isDeployTarget(entry.status) ? ` · ${deployAttribution(entry)}` : "";
  if (entry.status === "active")
    return `Deployed to ${envs}${entry.confirmed_at ? ` · confirmed ${timeAgo(entry.confirmed_at)}` : ""}${attribution}`;
  if (entry.status === "activating")
    return `Deploying to ${envs} (picked up on next sync, within a few minutes)${attribution}`;
  if (entry.status === "stalled")
    return `Deploy to ${envs} has not gone through — one of them is not picking it up${attribution}`;
  if (entry.status === "unpublishing")
    return `Undeploying from ${envs} — another revision was published (picked up on next sync)`;
  return `This version is not deployed to ${envs}`;
};

const PublishDialog: React.VFC<{
  target: string | null;
  name: string;
  publishedQuestionnaireId: number;
  onClose: () => void;
  onResult: (toast: { severity: "success" | "error"; message: string }) => void;
}> = ({ target, name, publishedQuestionnaireId, onClose, onResult }) => {
  const publishMutation = usePublishToGroup();

  const handlePublish = () => {
    if (!target) return;
    const group = target;
    publishMutation.mutate(
      { publishedQuestionnaireId, group },
      {
        onSuccess: () => {
          onResult({ severity: "success", message: `Deploying ${name} to ${groupEnvsLabel(group)}` });
          onClose();
        },
        onError: () => {
          onResult({ severity: "error", message: `Failed to deploy ${name} to ${groupEnvsLabel(group)} — try again.` });
        },
      }
    );
  };

  const handleClose = () => {
    publishMutation.reset();
    onClose();
  };

  return (
    <Dialog open={!!target} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Deploy to {target ? groupLabel(target) : ""}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          <strong>{name}</strong> (this exact version, #{publishedQuestionnaireId}) is deployed to{" "}
          <strong>{target ? groupEnvsLabel(target) : ""}</strong>. Those environments pick it up on their next sync
          (within a few minutes). Other questionnaires are unaffected.
        </Typography>
        {publishMutation.error ? <Typography color="error">Deploy failed — try again.</Typography> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handlePublish}
          disabled={publishMutation.isLoading}
          data-testid="confirm-publish-to-group"
        >
          {publishMutation.isLoading ? "Deploying..." : "Deploy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PublishedRolloutRow: React.VFC<{ name: string; publishedQuestionnaireId: number }> = ({
  name,
  publishedQuestionnaireId,
}) => {
  const historyQuery = usePublishedQuestionnaireHistory(name);
  const [target, setTarget] = useState<string | null>(null);
  const [toast, setToast] = useState<{ severity: "success" | "error"; message: string } | null>(null);

  if (historyQuery.isLoading) {
    return (
      <Box display="flex" alignItems="center" p={1}>
        <CircularProgress size={16} />
      </Box>
    );
  }
  const rollout = historyQuery.data?.find(entry => entry.id === publishedQuestionnaireId)?.rollout;
  if (historyQuery.error || !rollout) {
    return (
      <Typography variant="caption" color="error">
        Failed to load rollout status
      </Typography>
    );
  }

  return (
    <Box className={styles.row}>
      {rollout.map(entry => (
        <Box key={entry.group} className={styles.cell}>
          <RolloutStatusChip
            status={entry.status}
            label={groupLabel(entry.group)}
            tooltip={chipTooltip(entry)}
            suffix={entry.status === "active" && entry.confirmed_at ? ` · ${timeAgo(entry.confirmed_at)}` : ""}
          />
          {entry.status === "inactive" && (
            <Button size="small" color="primary" onClick={() => setTarget(entry.group)}>
              Deploy
            </Button>
          )}
        </Box>
      ))}

      <PublishDialog
        target={target}
        name={name}
        publishedQuestionnaireId={publishedQuestionnaireId}
        onClose={() => setTarget(null)}
        onResult={setToast}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

const styles = {
  row: css`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
  `,
  cell: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
};

export default PublishedRolloutRow;
